// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import type { AlarmBucket, AlarmSort } from '../../types/alarm';
import { useAlarms, useAlarmGroups } from '../../hooks/useAlarms';
import { usePersona } from '../../hooks/usePersona';
import { useActiveSite } from '../../hooks/useActiveSite';
import { SITE_LABELS } from '../../mocks/alarms';
import { PageHead, SevTag, Loading, DxTag } from '../../components/shell/ScreenHelpers';
import AlarmsTable from '../../components/shell/AlarmsTable';

// Site the AEM alarms API is called against for the live Carbon table.
const ALARM_API_SITE_ID = '019e8469-25fe-7a59-93c3-afb2a7c00000';

const BUCKET_TITLES: Record<string, string> = {
  all: 'All alarms',
  critical: 'Critical · P1',
  high: 'High · P2',
  closed: 'Closed',
};

const SORTS: { key: AlarmSort; label: string }[] = [
  { key: 'severity', label: 'Severity' },
  { key: 'time', label: 'Most recent' },
  { key: 'asset', label: 'Asset' },
];

export default function HomeAlarms() {
  const { bucket = 'all' } = useParams();
  const navigate = useNavigate();
  const { persona } = usePersona();
  const { siteId: operatorSiteId, siteLabel: operatorSiteLabel } = useActiveSite();
  const [layout, setLayout] = useState<'tiles' | 'list'>('tiles');
  const [sort, setSort] = useState<AlarmSort>('severity');
  const [group, setGroup] = useState<string>('all');

  const siteScope = persona === 'operator' ? operatorSiteId : undefined;
  const { groups } = useAlarmGroups(siteScope);
  const { alarms, loading } = useAlarms({
    siteId: siteScope,
    bucket: bucket as AlarmBucket,
    group,
    sort,
  });

  const title = BUCKET_TITLES[bucket] ?? 'Alarms';

  return (
    <div className="hs-page">
      <PageHead
        title={title}
        dateline={
          persona === 'operator' ? `${operatorSiteLabel} · live` : 'Fleet · all sites · live'
        }
        actions={
          <>
            <button
              type="button"
              className={`hs-pill${layout === 'tiles' ? ' active' : ''}`}
              onClick={() => setLayout('tiles')}
            >
              Tiles
            </button>
            <button
              type="button"
              className={`hs-pill${layout === 'list' ? ' active' : ''}`}
              onClick={() => setLayout('list')}
            >
              List
            </button>
          </>
        }
      />

      <AlarmsTable siteId={ALARM_API_SITE_ID} />

      <div className="hs-toolbar">
        {SORTS.map(s => (
          <button
            key={s.key}
            type="button"
            className={`hs-pill${sort === s.key ? ' active' : ''}`}
            onClick={() => setSort(s.key)}
          >
            {s.label}
          </button>
        ))}
        <span style={{ width: 12 }} />
        <button
          type="button"
          className={`hs-pill${group === 'all' ? ' active' : ''}`}
          onClick={() => setGroup('all')}
        >
          All groups
        </button>
        {groups.map(g => (
          <button
            key={g}
            type="button"
            className={`hs-pill${group === g ? ' active' : ''}`}
            onClick={() => setGroup(g)}
          >
            {g}
          </button>
        ))}
      </div>

      {loading ? (
        <Loading />
      ) : alarms.length === 0 ? null : (
        <div
          className={layout === 'tiles' ? 'hs-tiles' : ''}
          style={layout === 'list' ? { display: 'grid', gap: 8 } : undefined}
        >
          {alarms.map(a => (
            <button
              key={a.id}
              type="button"
              className="hs-card"
              style={{ borderLeftColor: `var(--sev${a.sev[1]})` }}
              onClick={() => navigate(`/alarms/${a.id}`)}
            >
              <div className="hs-card-row">
                <SevTag sev={a.sev} />
                {a.statusChip && <DxTag>{a.statusChip}</DxTag>}
                <span
                  style={{
                    marginLeft: 'auto',
                    fontSize: 11,
                    color: 'var(--text-helper)',
                    fontFamily: 'IBM Plex Mono, monospace',
                  }}
                >
                  {a.time}
                </span>
              </div>
              <div className="hs-card-asset">{a.asset}</div>
              <div className="hs-card-summary">{a.summary}</div>
              <div className="hs-card-meta">
                <span>{a.location}</span>
                {a.group && <span>· {a.group}</span>}
                {persona !== 'operator' && <span>· {SITE_LABELS[a.siteId] ?? a.siteId}</span>}
                {a.owner && (
                  <span>
                    · {a.owner} ({a.ownerStatus})
                  </span>
                )}
                {a.closed && a.outcome && <span>· {a.outcome}</span>}
              </div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
